import log from 'chameleon/ui-stack/utilities/log';

/**
 * Taken, CommonJS-ified, and heavily modified from:
 * https://github.com/flyingsparx/NodeDirectUploader
 */

/**
 * And then copied from https://github.com/odysseyscience/react-s3-uploader,
 * reproducing notice as indicated below:
 *
 * The MIT License (MIT)
 * Copyright (c) {{{year}}} {{{fullname}}} [sic]
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

S3Upload.prototype.signingUrl = '/sign-s3';
S3Upload.prototype.fileElement = null;
S3Upload.prototype.files = null;

S3Upload.prototype.onFinishS3Put = function onFinishS3Put(signResult) {
  log('base.onFinishS3Put()', signResult.publicUrl);
};

S3Upload.prototype.onProgress = function onProgress(percent, status) {
  log('base.onProgress()', percent, status);
};

S3Upload.prototype.onError = function onError(status) {
  log('base.onError()', status);
};

function S3Upload(options = {}) {
  const self = this;
  Object.entries(options).forEach((entry) => {
    const index = entry[0];
    const value = entry[1];
    self[index] = value;
  });
  const files = this.fileElement ? this.fileElement.files : this.files || [];
  this.handleFileSelect(files);
}

S3Upload.prototype.handleFileSelect = function handleFileSelect(files) {
  const result = [];
  this.onProgress(0, 'Waiting');
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    result.push(this.uploadFile(f));
  }
  return result;
};

S3Upload.prototype.createCORSRequest = function createCORSRequest(method, url) {
  let xhr = new XMLHttpRequest();

  if (xhr.withCredentials !== null) {
    xhr.open(method, url, true);
  } else if (typeof XDomainRequest !== 'undefined') {
    // eslint-disable-next-line no-undef
    xhr = new XDomainRequest();
    xhr.open(method, url);
  } else {
    xhr = null;
  }
  return xhr;
};

S3Upload.prototype.executeOnSignedUrl = function executeOnSignedUrl(
  file,
  callback,
) {
  const xhr = new XMLHttpRequest();
  const fileName = file.name.replace(/\s+/g, '_');
  let queryString = `?objectName=${fileName}&contentType=${file.type}`;
  if (this.signingUrlQueryParams) {
    const { signingUrlQueryParams } = this;
    Object.keys(signingUrlQueryParams).forEach((key) => {
      const val = signingUrlQueryParams[key];
      queryString += `&${key}=${val}`;
    });
  }
  xhr.open('GET', this.signingUrl + queryString, true);
  if (this.signingUrlHeaders) {
    const { signingUrlHeaders } = this;
    Object.keys(signingUrlHeaders).forEach((key) => {
      const val = signingUrlHeaders[key];
      xhr.setRequestHeader(key, val);
    });
  }
  xhr.overrideMimeType &&
    xhr.overrideMimeType('text/plain; charset=x-user-defined');
  xhr.onreadystatechange = () => {
    if (xhr.readyState === 4 && xhr.status === 200) {
      let result;
      try {
        result = JSON.parse(xhr.responseText);
      } catch (error) {
        this.onError(
          `Invalid signing server response JSON: ${xhr.responseText}`,
        );
        return false;
      }
      return callback(result);
    } else if (xhr.readyState === 4 && xhr.status !== 200) {
      return this.onError(
        `Could not contact request signing server. Status = ${xhr.status}`,
      );
    }
  };
  return xhr.send();
};

S3Upload.prototype.uploadToS3 = function uploadToS3(file, signResult) {
  const xhr = this.createCORSRequest('PUT', signResult.signedUrl);
  if (!xhr) {
    this.onError('CORS not supported');
  } else {
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 304) {
        this.onProgress(100, 'Upload completed.');
        const headers = xhr.getAllResponseHeaders();
        return this.onFinishS3Put(signResult, headers);
      }

      return this.onError(`Upload error: ${xhr.status}`);
    };

    xhr.onerror = () => this.onError('XHR error.');

    xhr.upload.onprogress = function uploadOnprogress(e) {
      if (e.lengthComputable) {
        const percentLoaded = Math.round((e.loaded / e.total) * 100);
        return this.onProgress(
          percentLoaded,
          percentLoaded === 100 ? 'Finalizing' : 'Uploading',
        );
      }
    }.bind(this);
  }

  let fileType = file.type;
  switch (file.type) {
    case 'media/audio':
    case 'audio/mp3':
      fileType = 'audio/mpeg';
      break;
    case 'application/x-zip-compressed':
      fileType = 'application/zip';
      break;
    default:
      break;
  }

  xhr.setRequestHeader('Content-Type', fileType);

  if (this.contentDisposition) {
    let disposition = this.contentDisposition;
    if (disposition === 'auto') {
      if (file.type.substr(0, 6) === 'image/') {
        disposition = 'inline';
      } else {
        disposition = 'attachment';
      }
    }
    const fileName = file.name.replace(/\s+/g, '_');
    xhr.setRequestHeader(
      'Content-Disposition',
      `${disposition}; filename=${fileName}`,
    );
  }
  if (this.uploadRequestHeaders) {
    const { uploadRequestHeaders } = this;
    Object.keys(uploadRequestHeaders).forEach((key) => {
      const val = uploadRequestHeaders[key];
      xhr.setRequestHeader(key, val);
    });
  } else {
    xhr.setRequestHeader('x-amz-acl', 'public-read');
  }
  this.httprequest = xhr;
  return xhr.send(file);
};

S3Upload.prototype.uploadFile = function uploadFile(file) {
  return this.executeOnSignedUrl(file, (signResult) =>
    this.uploadToS3(file, signResult),
  );
};

S3Upload.prototype.abortUpload = function abortUpload() {
  this.httprequest && this.httprequest.abort();
};

export default S3Upload;
